import type {
  LegacyDemographicChanges,
  ReportCorrectionChange,
  ReportSupplementEntry,
} from "../../types/reportTemplate";
import { stripHtml } from "../../utils/helpers";

interface NamedCorrectionChange extends ReportCorrectionChange {
  field: string;
}

const FIELD_LABELS: Record<string, string> = {
  patientFirstName: "Patient First Name",
  patientLastName: "Patient Last Name",
  dateOfBirth: "Date of Birth",
  dateCollected: "Date of Service",
};

const CLIENT_PROVIDER_FIELDS = new Set([
  "caseInfo.sendingPracticeId",
  "caseInfo.physicianId",
  "caseInfo.surgeryCenterLabId",
  "caseInfo.submittingLabId",
]);

export function buildCorrectionDescriptions(
  entry: ReportSupplementEntry
): string[] {
  const evidenceChanges = correctionEvidenceChanges(entry);
  const changes =
    evidenceChanges.length > 0
      ? evidenceChanges
      : legacyCorrectionChanges(entry);
  const descriptions = changes.map(correctionDescription);
  if (descriptions.length > 0) return descriptions;
  return [
    stripHtml(entry.contentHtml || entry.comments || "") ||
      "Patient demographic information was corrected.",
  ];
}

function correctionEvidenceChanges(
  entry: ReportSupplementEntry
): NamedCorrectionChange[] {
  const changes = entry.correctionEvidence?.changes;
  if (!changes) return [];
  if (!Array.isArray(changes)) return correctionRecordChanges(changes);
  return changes.map((change, index) => ({
    ...change,
    field: correctionFieldName(change, index),
  }));
}

function correctionRecordChanges(
  changes: Record<string, ReportCorrectionChange>
): NamedCorrectionChange[] {
  return Object.entries(changes).map(([field, change]) => ({
    ...change,
    field,
  }));
}

function correctionFieldName(
  change: ReportCorrectionChange,
  index: number
): string {
  return (
    change.field ||
    change.fieldName ||
    change.fieldPath ||
    change.label ||
    `Correction ${index + 1}`
  );
}

function legacyCorrectionChanges(
  entry: ReportSupplementEntry
): NamedCorrectionChange[] {
  const changes = entry.demographicChanges;
  if (!changes) return [];
  if (!isLegacyChanges(changes)) return correctionRecordChanges(changes);
  return legacyCorrectionFields(changes);
}

function legacyCorrectionFields(
  changes: LegacyDemographicChanges
): NamedCorrectionChange[] {
  const fields = new Set([
    ...Object.keys(changes.before),
    ...Object.keys(changes.after),
  ]);
  return Array.from(fields).map((field) => ({
    field,
    before: changes.before[field as keyof typeof changes.before],
    after: changes.after[field as keyof typeof changes.after],
  }));
}

function isLegacyChanges(
  changes: ReportSupplementEntry["demographicChanges"]
): changes is LegacyDemographicChanges {
  if (!changes || !("before" in changes) || !("after" in changes)) {
    return false;
  }
  return typeof changes.before === "object" && changes.before !== null;
}

function correctionDescription(change: NamedCorrectionChange): string {
  const label = correctionFieldLabel(change);
  const before = plainCorrectionValue(change.before);
  const after = plainCorrectionValue(change.after);
  if (!before && after) return `${label} added: ${after}`;
  if (before && !after) return `${label} removed: ${before}`;
  if (CLIENT_PROVIDER_FIELDS.has(change.field)) {
    return `${label} updated from ${before || "empty"} to ${after || "empty"}`;
  }
  return `${label} corrected from ${before || "empty"} to ${after || "empty"}`;
}

function correctionFieldLabel(change: NamedCorrectionChange): string {
  if (change.label) return stripHtml(change.label);
  if (FIELD_LABELS[change.field]) return FIELD_LABELS[change.field];
  return change.field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function plainCorrectionValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(plainCorrectionValue).join(", ");
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, entry]) => `${key}: ${plainCorrectionValue(entry)}`)
      .join(", ");
  }
  return stripHtml(value == null ? "" : String(value)).trim();
}
